import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequest } from '../../hooks/useRequest';
import { ChatShareLink, shareChat } from '../../utils/axios/api';

const MAX_SHARE_SECONDS = 60 * 60 * 24 * 365 * 10;

const DURATION_OPTIONS = [
  { id: 'unlimited', seconds: null },
  { id: 'hour', seconds: 60 * 60 },
  { id: 'day', seconds: 60 * 60 * 24 },
  { id: 'week', seconds: 60 * 60 * 24 * 7 },
  { id: 'month', seconds: 60 * 60 * 24 * 30 },
  { id: 'custom', seconds: null },
] as const;

type DurationId = (typeof DURATION_OPTIONS)[number]['id'];
type DurationUnit = 'minutes' | 'hours' | 'days';

const UNIT_SECONDS: Record<DurationUnit, number> = {
  minutes: 60,
  hours: 60 * 60,
  days: 60 * 60 * 24,
};

interface ChatShareButtonProps {
  chatId: string;
}

export function ChatShareButtonComponent({ chatId }: ChatShareButtonProps) {
  const { t } = useTranslation();
  const shareRequest = useRequest<ChatShareLink>();
  const [isOpen, setIsOpen] = useState(false);
  const [durationId, setDurationId] = useState<DurationId>('unlimited');
  const [customAmount, setCustomAmount] = useState('1');
  const [customUnit, setCustomUnit] = useState<DurationUnit>('hours');
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setDurationId('unlimited');
    setCustomAmount('1');
    setCustomUnit('hours');
    setLink(null);
    setCopied(false);
    setValidationError(null);
  }, []);

  const resolveSeconds = useCallback((): number | null | undefined => {
    if (durationId !== 'custom') {
      return DURATION_OPTIONS.find((option) => option.id === durationId)
        ?.seconds ?? null;
    }

    const amount = Number(customAmount);

    if (!Number.isInteger(amount) || amount < 1) {
      return undefined;
    }

    const seconds = amount * UNIT_SECONDS[customUnit];

    if (seconds > MAX_SHARE_SECONDS) {
      return undefined;
    }

    return seconds;
  }, [customAmount, customUnit, durationId]);

  const handleCreate = useCallback(async () => {
    const seconds = resolveSeconds();

    if (seconds === undefined) {
      setValidationError(t('chats.share.errors.duration'));
      return;
    }

    setValidationError(null);
    setCopied(false);

    const result = await shareRequest.fetch(() => shareChat(chatId, seconds));

    if (!result) {
      return;
    }

    setLink(`${window.location.origin}/shared-chat/${result.uid}`);
  }, [chatId, resolveSeconds, shareRequest, t]);

  const handleCopy = useCallback(async () => {
    if (!link) {
      return;
    }

    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setValidationError(null);
    } catch {
      setValidationError(t('chats.share.errors.copyFailed'));
    }
  }, [link, t]);

  const requestError = shareRequest.exception
    ? t(`exceptions.${shareRequest.exception}`)
    : null;

  return (
    <>
      <Tooltip title={t('chats.share.button')}>
        <IconButton
          size="small"
          aria-label={t('chats.share.button')}
          onClick={() => {
            reset();
            setIsOpen(true);
          }}
        >
          <ShareOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={isOpen}
        onClose={() => {
          if (!shareRequest.isLoading) {
            setIsOpen(false);
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('chats.share.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t('chats.share.hint')}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="chat-share-duration-label">
                {t('chats.share.duration')}
              </InputLabel>
              <Select
                labelId="chat-share-duration-label"
                label={t('chats.share.duration')}
                value={durationId}
                onChange={(event) => {
                  setDurationId(event.target.value as DurationId);
                  setLink(null);
                  setCopied(false);
                  setValidationError(null);
                }}
              >
                {DURATION_OPTIONS.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {t(`chats.share.${option.id}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {durationId === 'custom' ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label={t('chats.share.amount')}
                  type="number"
                  value={customAmount}
                  onChange={(event) => {
                    setCustomAmount(event.target.value);
                    setLink(null);
                    setCopied(false);
                    setValidationError(null);
                  }}
                  slotProps={{ htmlInput: { min: 1, step: 1 } }}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel id="chat-share-unit-label">
                    {t('chats.share.unit')}
                  </InputLabel>
                  <Select
                    labelId="chat-share-unit-label"
                    label={t('chats.share.unit')}
                    value={customUnit}
                    onChange={(event) => {
                      setCustomUnit(event.target.value as DurationUnit);
                      setLink(null);
                      setCopied(false);
                    }}
                  >
                    <MenuItem value="minutes">{t('chats.share.minutes')}</MenuItem>
                    <MenuItem value="hours">{t('chats.share.hours')}</MenuItem>
                    <MenuItem value="days">{t('chats.share.days')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            ) : null}
            {link ? (
              <TextField
                label={t('chats.share.link')}
                value={link}
                slotProps={{
                  input: {
                    readOnly: true,
                    endAdornment: (
                      <Tooltip
                        title={
                          copied
                            ? t('chats.share.copied')
                            : t('chats.share.copy')
                        }
                      >
                        <IconButton
                          aria-label={t('chats.share.copy')}
                          onClick={() => {
                            void handleCopy();
                          }}
                          edge="end"
                        >
                          <ContentCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                  },
                }}
              />
            ) : null}
            {validationError || requestError ? (
              <Typography variant="body2" color="error">
                {validationError ?? requestError}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsOpen(false)}
            disabled={shareRequest.isLoading}
          >
            {t('chats.share.close')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              void handleCreate();
            }}
            disabled={shareRequest.isLoading}
          >
            {t('chats.share.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
